// Resilient fetch for first-load-critical public GETs.
//
// A single-shot fetch on a landing page turns any transient failure — a dropped
// first TLS connection, a momentary worker/D1 5xx, a flaky mobile network — into
// a permanent "couldn't load" screen for that visitor. This helper retries the
// request a few times with backoff before giving up, so one blip never strands
// a real prospect arriving from a campaign email.
//
// Retryable: network errors (fetch throws), HTTP 5xx, and 429.
// Not retryable: other 4xx (404, 403, …) — those are real answers.

export interface FetchRetryOptions {
  /** Total attempts including the first. Default 3. */
  attempts?: number;
  /** Base delay in ms before the first retry; doubles each retry. Default 600. */
  baseDelayMs?: number;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export async function fetchWithRetry(
  input: RequestInfo | URL,
  init?: RequestInit,
  opts: FetchRetryOptions = {},
): Promise<Response> {
  const attempts = opts.attempts ?? 3;
  const baseDelayMs = opts.baseDelayMs ?? 600;
  let lastError: unknown;

  for (let attempt = 0; attempt < attempts; attempt++) {
    if (attempt > 0) await sleep(baseDelayMs * 2 ** (attempt - 1));
    try {
      const res = await fetch(input, init);
      const retryable = res.status >= 500 || res.status === 429;
      if (!retryable || attempt === attempts - 1) return res;
      lastError = new Error(`HTTP ${res.status}`);
    } catch (err) {
      lastError = err;
      if (attempt === attempts - 1) throw err;
    }
  }
  throw lastError instanceof Error ? lastError : new Error('fetch failed');
}
